import * as React from "react"
import { useMemo } from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"

/**
 * Wires controls inside a <Field> together automatically:
 * - control gets the id the label points at,
 * - error text gets an id the control announces via aria-describedby.
 * Fixes "label does nothing / screen reader says 'combobox' with no name".
 */
type FieldControlIds = { controlId: string; errorId: string }
const FieldControlContext = React.createContext<FieldControlIds | null>(null)

export function useFieldControlIds(): FieldControlIds | null {
  return React.useContext(FieldControlContext)
}

/** Merges caller-provided ids with the ones generated by the parent <Field>. */
export function useFieldAria(
  props: React.AriaAttributes & { id?: string },
): React.AriaAttributes & { id?: string } {
  const ids = React.useContext(FieldControlContext)
  const ownDescribedBy = props["aria-describedby"]
  if (!ids) return props
  const describedBy = [ownDescribedBy, ids.errorId].filter(Boolean).join(" ")
  return {
    ...props,
    id: props.id ?? ids.controlId,
    "aria-describedby": describedBy || undefined,
  }
}

function FieldSet({ className, ...props }: React.ComponentProps<"fieldset">) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn(
        "flex flex-col gap-6",
        "has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3",
        className
      )}
      {...props}
    />
  )
}

function FieldLegend({
  className,
  variant = "legend",
  ...props
}: React.ComponentProps<"legend"> & { variant?: "legend" | "label" }) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn(
        "mb-3 font-medium",
        "data-[variant=legend]:text-base",
        "data-[variant=label]:text-sm",
        className
      )}
      {...props}
    />
  )
}

function FieldGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-group"
      className={cn(
        "group/field-group @container/field-group flex w-full flex-col gap-7 data-[slot=checkbox-group]:gap-3 [&>[data-slot=field-group]]:gap-4",
        className
      )}
      {...props}
    />
  )
}

const fieldVariants = cva(
  "group/field data-[invalid=true]:text-destructive flex w-full gap-3",
  {
    variants: {
      orientation: {
        vertical: ["flex-col [&>*]:w-full [&>.sr-only]:w-auto"],
        horizontal: [
          "flex-row items-center",
          "[&>[data-slot=field-label]]:flex-auto",
          "has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px has-[>[data-slot=field-content]]:items-start",
        ],
        responsive: [
          "@md/field-group:flex-row @md/field-group:items-center @md/field-group:[&>*]:w-auto flex-col [&>*]:w-full [&>.sr-only]:w-auto",
          "@md/field-group:[&>[data-slot=field-label]]:flex-auto",
          "@md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px",
        ],
      },
    },
    defaultVariants: {
      orientation: "vertical",
    },
  }
)

function Field({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof fieldVariants>) {
  const baseId = React.useId()
  const ids = useMemo(
    () => ({ controlId: `${baseId}-control`, errorId: `${baseId}-error` }),
    [baseId]
  )
  return (
    <FieldControlContext.Provider value={ids}>
      <div
        role="group"
        data-slot="field"
        data-orientation={orientation}
        className={cn(fieldVariants({ orientation }), className)}
        {...props}
      />
    </FieldControlContext.Provider>
  )
}

function FieldContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-content"
      className={cn(
        "group/field-content flex flex-1 flex-col gap-1.5 leading-snug",
        className
      )}
      {...props}
    />
  )
}

function FieldLabel({
  className,
  required,
  htmlFor,
  children,
  ...props
}: React.ComponentProps<typeof Label> & { required?: boolean }) {
  const ids = React.useContext(FieldControlContext)
  return (
    <Label
      data-slot="field-label"
      htmlFor={htmlFor ?? ids?.controlId}
      className={cn(
        "group/field-label peer/field-label flex w-fit gap-2 leading-snug group-data-[disabled=true]/field:opacity-50",
        "has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col has-[>[data-slot=field]]:rounded-md has-[>[data-slot=field]]:border [&>[data-slot=field]]:p-4",
        "has-data-[state=checked]:bg-primary/5 has-data-[state=checked]:border-primary",
        className
      )}
      {...props}
    >
      {children}
      {required ? (
        <>
          <span aria-hidden="true" className="text-destructive">*</span>
          <span className="sr-only">{t("form_required_field")}</span>
        </>
      ) : null}
    </Label>
  )
}

function FieldTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="field-label"
      className={cn(
        "flex w-fit items-center gap-2 text-sm font-medium leading-snug group-data-[disabled=true]/field:opacity-50",
        className
      )}
      {...props}
    />
  )
}

function FieldDescription({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="field-description"
      className={cn(
        "text-muted-foreground text-sm font-normal leading-normal group-has-[[data-orientation=horizontal]]/field:text-balance",
        "nth-last-2:-mt-1 last:mt-0 [[data-variant=legend]+&]:-mt-1.5",
        "[&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4",
        className
      )}
      {...props}
    />
  )
}

function FieldSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  children?: React.ReactNode
}) {
  return (
    <div
      data-slot="field-separator"
      data-content={!!children}
      className={cn(
        "relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2",
        className
      )}
      {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children && (
        <span
          className="bg-background text-muted-foreground relative mx-auto block w-fit px-2"
          data-slot="field-separator-content"
        >
          {children}
        </span>
      )}
    </div>
  )
}

function FieldError({
  className,
  children,
  errors,
  ...props
}: React.ComponentProps<"div"> & {
  errors?: Array<{ message?: string } | undefined>
}) {
  const content = useMemo(() => {
    if (children) {
      return children
    }

    if (!errors) {
      return null
    }

    const visibleErrors = errors.filter(
      (error): error is { message: string } => Boolean(error?.message)
    )

    if (visibleErrors.length === 0) {
      return null
    }

    if (visibleErrors.length === 1) {
      return t(visibleErrors[0].message)
    }

    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {visibleErrors.map(
          (error, index) =>
            <li key={index}>{t(error.message)}</li>
        )}
      </ul>
    )
  }, [children, errors])

  if (!content) {
    return null
  }

  return (
    <FieldErrorText className={className} {...props}>
      {content}
    </FieldErrorText>
  )
}

/** The always-mounted flavour of FieldError: reserves the announced-by id so
 * inputs may reference it even before an error appears. */
function FieldErrorSlot() {
  const ids = useFieldControlIds()
  return ids ? <div id={ids.errorId} hidden aria-hidden="true" /> : null
}

function FieldErrorText({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const ids = React.useContext(FieldControlContext)
  return (
    <div
      role="alert"
      id={ids?.errorId}
      data-slot="field-error"
      className={cn("text-destructive text-sm font-normal", className)}
      {...props}
    />
  )
}

export {
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
  FieldErrorSlot,
  FieldGroup,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldContent,
  FieldTitle,
}
